import { type Article } from '~/types/article';
import { type PublicationContext } from '~/types';
import { type BrazeBannerCard, type InlineMaFrame } from '~/types/articleInline';

export default class InlineBrazeBannerCardProcessor {
  private articleBannerConfig: any;

  constructor () {
    this.articleBannerConfig = getBrazeConfig().bannerConfig?.article;
  }

  process (article: Article) {
    if (
      !article.body ||
      article.body.length <= 0 ||
      !this.articleBannerConfig?.inline ||
      this.articleBannerConfig.inline.length <= 0
    ) { return; }

    const meta = article?.metadata as any;

    // Never place inline banner cards when the article disables banner cards
    if (meta?.removeBannerCards === true) {
      this.removeMaFramePlaceholders(article);
      return;
    }

    if (meta?.manuallyPlacedAds === true) {
      this.placeManually(article);
    } else {
      this.placeAutomatically(article);
    }
  }

  /**
   * Manual placement: walk the body and replace each bare { type: 'ma_frame' } placeholder
   * in order with the corresponding BrazeBannerCard config from articleBannerConfig.inline[].
   * Placeholders with no matching config, or where shouldRenderBannerCard is false, are removed.
   */
  private placeManually (article: Article) {
    const inlineCards: BrazeBannerCard[] = this.articleBannerConfig.inline;
    let cardConfigIndex = 0;

    for (let i = 0; i < article.body.length; i++) {
      const element = article.body[i] as any;

      // Only target bare ma_frame placeholders — skip already-injected ones
      if (element?.type !== 'ma_frame' || element?.injected === true) { continue; }

      if (cardConfigIndex >= inlineCards.length) {
        // No more configs — remove remaining placeholders
        article.body.splice(i, 1);
        i--;
        continue;
      }

      const cardConfig = inlineCards[cardConfigIndex];
      cardConfigIndex++;

      if (!shouldRenderBannerCard(cardConfig)) {
        // Card should not render — remove the placeholder
        article.body.splice(i, 1);
        i--;
        continue;
      }

      // Replace the placeholder with the resolved ma_frame element (has id for DnMaFrame)
      const injectedCard: InlineMaFrame = {
        type: 'ma_frame',
        id: cardConfig.id,
        supportedDevices: cardConfig.supportedDevices,
        injected: true
      };

      article.body.splice(i, 1, injectedCard);
    }
  }

  /**
   * Automatic placement: insert banner cards after reaching the configured
   * paragraph count threshold (original behaviour).
   */
  private placeAutomatically (article: Article) {
    let bannerCardPosition = 0;

    this.articleBannerConfig.inline.forEach((bannerCard: BrazeBannerCard) => {
      if (!shouldRenderBannerCard(bannerCard)) { return; }
      try {
        let paragraphCount = 0;
        const maxParagraphCount = bannerCard.paragraphCount[getDeviceType()];
        for (let i = bannerCardPosition; i <= article.body.length; i++) {
          if ((article.body[i] as any)?.type && article.body[i].type === 'text') {
            paragraphCount++;
            if (paragraphCount >= maxParagraphCount) {
              bannerCardPosition = i + 1;
              const injectedCard: InlineMaFrame = {
                type: 'ma_frame',
                id: bannerCard.id,
                supportedDevices: bannerCard.supportedDevices,
                injected: true
              };
              article.body.splice(bannerCardPosition, 0, injectedCard);
              break;
            }
          }
        }
      } catch (e) {}
    });
  }

  /**
   * Remove all bare ma_frame placeholders from the body (used when banner cards are disabled).
   */
  private removeMaFramePlaceholders (article: Article) {
    for (let i = 0; i < article.body.length; i++) {
      const element = article.body[i] as any;
      if (element?.type === 'ma_frame' && element?.injected !== true) {
        article.body.splice(i, 1);
        i--;
      }
    }
  }
}
